import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Settings, 
  User, 
  Paperclip, 
  Calendar, 
  Users,
  ClipboardList,
  Building2,
  UserCog,
  FileText,
  Mail,
  Bell,
  MessageCircle,
  Megaphone,
  UsersRound,
  Sparkles,
  Presentation,
  BookOpen
} from 'lucide-react';
import AttachmentsSection from './AttachmentsSection';
import MinistryEnrollmentsSection from './MinistryEnrollmentsSection';
import AdminUsersSection from './AdminUsersSection';
import AuditLogsSection from './AuditLogsSection';
import DepartmentEmailModule from './DepartmentEmailModule';
import AnnouncementsSection from './AnnouncementsSection';
import ContactMessagesSection from './ContactMessagesSection';
import MeetTheTeamSection from './MeetTheTeamSection';
import InspirationalSection from './InspirationalSection';
import PlatformPresentation from '../../pages/PlatformPresentation';
import Handbook from '../../pages/Handbook';

// Import ServiceScheduleSection - it's passed as a render prop since it needs external state
// Settings section will be inline since it's simple

const SECRETARIAL_TABS = [
  { id: 'schedule', label: 'Service Schedule', icon: Calendar, color: 'from-green-500 to-emerald-500' },
  { id: 'members', label: 'Members', icon: User, color: 'from-slate-500 to-slate-600' },
  { id: 'team', label: 'Meet the Team', icon: UsersRound, color: 'from-violet-500 to-purple-600' },
  { id: 'communications', label: 'Communications', icon: Megaphone, color: 'from-indigo-500 to-purple-500' },
  { id: 'attachments', label: 'Attachments', icon: Paperclip, color: 'from-pink-500 to-rose-500' },
  { id: 'enrollments', label: 'Enrollments', icon: Users, color: 'from-teal-500 to-cyan-500' },
  { id: 'email', label: 'Email', icon: Mail, color: 'from-blue-500 to-indigo-500' },
  { id: 'users', label: 'Admin Users', icon: UserCog, color: 'from-cyan-500 to-blue-500', adminOnly: true },
  { id: 'audit', label: 'Audit Logs', icon: FileText, color: 'from-slate-600 to-slate-700', adminOnly: true },
  { id: 'inspirational', label: 'Inspirational', icon: Sparkles, color: 'from-amber-400 to-yellow-500' },
  { id: 'presentation', label: 'Presentation', icon: Presentation, color: 'from-fuchsia-500 to-pink-500' },
  { id: 'handbook', label: 'Handbook', icon: BookOpen, color: 'from-sky-500 to-blue-500' },
  { id: 'settings', label: 'Settings', icon: Settings, color: 'from-amber-500 to-orange-500', adminOnly: true }
];

const SecretarialAdminSection = ({ 
  adminUser, 
  serviceSchedules, 
  setServiceSchedules,
  renderServiceSchedule,
  renderMembers,
  renderSettings
}) => {
  const [activeTab, setActiveTab] = useState('schedule');
  const isMainAdmin = adminUser?.role === 'main_admin';

  // Filter tabs based on permissions
  const visibleTabs = SECRETARIAL_TABS.filter(tab => {
    if (tab.adminOnly && !isMainAdmin) return false;
    return true;
  });

  return (
    <div className="space-y-6" data-testid="secretarial-admin-section">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-slate-600 via-slate-700 to-slate-800 rounded-2xl sm:rounded-3xl p-4 sm:p-6 text-white shadow-xl"
      >
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
          <div className="p-3 rounded-xl bg-white/20 backdrop-blur-md w-fit">
            <Building2 className="w-6 h-6 sm:w-8 sm:h-8" />
          </div>
          <div className="flex-1">
            <h2 className="text-xl sm:text-2xl font-bold">Secretarial & Admin</h2>
            <p className="text-slate-300 text-sm sm:text-base">Manage schedules, members, enrollments, users, and settings</p>
          </div>
        </div>
      </motion.div>

      {/* Sub-tabs */}
      <div className="bg-white rounded-2xl p-4 shadow-lg border border-slate-100">
        <div className="flex gap-2 overflow-x-auto pb-1">
          {visibleTabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`secretarial-tab-${tab.id}`}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium whitespace-nowrap transition-all ${
                  activeTab === tab.id
                    ? `bg-gradient-to-r ${tab.color} text-white shadow-lg`
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <AnimatePresence mode="wait">
        {activeTab === 'schedule' && (
          <motion.div
            key="schedule"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            {renderServiceSchedule()}
          </motion.div>
        )}
        {activeTab === 'members' && (
          <motion.div
            key="members"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            {renderMembers()}
          </motion.div>
        )}
        {activeTab === 'team' && (
          <motion.div
            key="team"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <MeetTheTeamSection adminUser={adminUser} />
          </motion.div>
        )}
        {activeTab === 'communications' && (
          <motion.div
            key="communications"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <CommunicationsSubSection adminUser={adminUser} />
          </motion.div>
        )}
        {activeTab === 'attachments' && (
          <motion.div
            key="attachments"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <AttachmentsSection />
          </motion.div>
        )}
        {activeTab === 'enrollments' && (
          <motion.div
            key="enrollments"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <MinistryEnrollmentsSection adminUser={adminUser} />
          </motion.div>
        )}
        {activeTab === 'email' && (
          <motion.div
            key="email"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <DepartmentEmailModule department="secretary" adminUser={adminUser} compact={false} />
          </motion.div>
        )}
        {activeTab === 'users' && isMainAdmin && (
          <motion.div
            key="users"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <AdminUsersSection />
          </motion.div>
        )}
        {activeTab === 'audit' && isMainAdmin && (
          <motion.div
            key="audit"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <AuditLogsSection />
          </motion.div>
        )}
        {activeTab === 'inspirational' && (
          <motion.div
            key="inspirational"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <InspirationalSection />
          </motion.div>
        )}
        {activeTab === 'presentation' && (
          <motion.div
            key="presentation"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="bg-white rounded-2xl shadow-lg border border-slate-100 overflow-hidden"
            style={{ height: '80vh' }}
          >
            <PlatformPresentation embedded />
          </motion.div>
        )}
        {activeTab === 'handbook' && (
          <motion.div
            key="handbook"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <Handbook embedded />
          </motion.div>
        )}
        {activeTab === 'settings' && isMainAdmin && (
          <motion.div
            key="settings"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            {renderSettings()}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SecretarialAdminSection;

// Communications sub-section component
const CommunicationsSubSection = ({ adminUser }) => {
  const [commTab, setCommTab] = useState('announcements');
  
  const COMM_TABS = [
    { id: 'announcements', label: 'Announcements', icon: Bell, color: 'from-indigo-500 to-blue-500' },
    { id: 'messages', label: 'Messages', icon: MessageCircle, color: 'from-purple-500 to-violet-500' },
    { id: 'email', label: 'Email', icon: Mail, color: 'from-blue-500 to-indigo-500' }
  ];

  return (
    <div className="space-y-4">
      {/* Communications Header */}
      <div className="bg-gradient-to-r from-indigo-500 via-purple-500 to-violet-500 rounded-xl p-4 text-white">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-white/20">
            <Megaphone className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-bold text-lg">Communications</h3>
            <p className="text-indigo-100 text-sm">Manage announcements, messages, and emails</p>
          </div>
        </div>
      </div>
      
      {/* Sub-tabs */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {COMM_TABS.map(tab => {
          const Icon = tab.icon;
          return (
            <motion.button
              key={tab.id}
              onClick={() => setCommTab(tab.id)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg font-medium whitespace-nowrap transition-all text-sm ${
                commTab === tab.id
                  ? `bg-gradient-to-r ${tab.color} text-white shadow-lg`
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </motion.button>
          );
        })}
      </div>

      {/* Content */}
      <AnimatePresence mode="wait">
        {commTab === 'announcements' && (
          <motion.div
            key="announcements"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            <AnnouncementsSection />
          </motion.div>
        )}
        {commTab === 'messages' && (
          <motion.div
            key="messages"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            <ContactMessagesSection adminUser={adminUser} />
          </motion.div>
        )}
        {commTab === 'email' && (
          <motion.div
            key="email"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            <DepartmentEmailModule department="communications" adminUser={adminUser} compact={false} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
